import React, { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import {
  DirectionsCar as CarIcon,
  PendingActions as PendingIcon,
  CheckCircle as CompletedIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  Psychology as AiIcon,
  PeopleAlt as DriversIcon,
} from '@mui/icons-material';
import {
  AttachMoney as AttachMoneyIcon,
  LocationOn as LocationOnIcon,
  Groups as GroupsIcon2,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocket } from '../contexts/SocketContext';
import { rideService } from '../services/rideService';
import { whatsappService } from '../services/whatsappService';
import api from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { Ride } from '../types';

function AnalyticsBarChart({
  rows,
  valueKey,
  labelKey,
  color = '#2196f3',
  formatValue = (v: number) => String(v),
}: {
  rows: Record<string, any>[];
  valueKey: string;
  labelKey: string;
  color?: string;
  formatValue?: (v: number) => string;
}) {
  const max = Math.max(...rows.map((r) => r[valueKey] || 0), 1);
  return (
    <Stack spacing={0.5}>
      {rows.map((row) => (
        <Box key={row[labelKey]} display="flex" alignItems="center" gap={1}>
          <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }} noWrap>
            {row[labelKey]}
          </Typography>
          <Box
            sx={{
              height: 18,
              width: `${((row[valueKey] || 0) / max) * 100}%`,
              minWidth: 4,
              bgcolor: color,
              borderRadius: 1,
              transition: 'width 0.4s ease',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {formatValue(row[valueKey] || 0)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

const RECOMMENDATION_COLOR: Record<string, 'success' | 'warning' | 'default'> = {
  high: 'success',
  medium: 'warning',
  low: 'default',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { whatsappStatus, newRideCount, clearNewRideCount, socket } = useSocket();
  const [recentRides, setRecentRides] = useState<Ride[]>([]);
  const [liveMessageCount, setLiveMessageCount] = useState(0);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [costDays, setCostDays] = useState(14);
  const [heatmapDays, setHeatmapDays] = useState(30);
  const queryClient = useQueryClient();

  // AI queue status
  const { data: aiStatus } = useQuery({
    queryKey: ['aiStatus'],
    queryFn: () => api.get('/message-queue/ai/status').then((r) => r.data),
    refetchInterval: 15000,
  });

  // Online driver count
  const { data: onlineDrivers } = useQuery({
    queryKey: ['onlineDriverCount'],
    queryFn: () => api.get('/users/online-count').then((r) => r.data),
    refetchInterval: 30000,
  });

  // Analytics queries (loaded on-demand when section is expanded)
  const { data: costData, isLoading: costLoading, error: costError } = useQuery({
    queryKey: ['aiCostStats', costDays],
    queryFn: () => rideService.getAiCostStats(costDays),
    enabled: analyticsOpen,
    refetchInterval: analyticsOpen ? 60_000 : false,
  });

  const { data: heatmap, isLoading: heatmapLoading } = useQuery({
    queryKey: ['heatmapData', heatmapDays],
    queryFn: () => rideService.getHeatmapData(heatmapDays),
    enabled: analyticsOpen,
    refetchInterval: analyticsOpen ? 120_000 : false,
  });

  const { data: suggestions, isLoading: suggestionsLoading } = useQuery({
    queryKey: ['groupSuggestions'],
    queryFn: () => rideService.getGroupSuggestions(30),
    enabled: analyticsOpen,
    refetchInterval: analyticsOpen ? 300_000 : false,
  });

  // Toggle AI on/off
  const aiToggle = useMutation({
    mutationFn: (enable: boolean) =>
      api.post(enable ? '/message-queue/ai/resume' : '/message-queue/ai/pause'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aiStatus'] }),
  });

  // Toggle queue processing on/off
  const queueToggle = useMutation({
    mutationFn: (enable: boolean) =>
      api.post(enable ? '/message-queue/resume' : '/message-queue/pause'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['aiStatus'] }),
  });

  // Listen for live messages
  useEffect(() => {
    if (!socket) return;

    const handleLiveMessage = () => {
      setLiveMessageCount(prev => prev + 1);
    };

    socket.on('whatsapp:message', handleLiveMessage);
    socket.on('whatsapp:liveMessage', handleLiveMessage);

    return () => {
      socket.off('whatsapp:message', handleLiveMessage);
      socket.off('whatsapp:liveMessage', handleLiveMessage);
    };
  }, [socket]);

    // Fetch ride statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['rideStats'],
    queryFn: rideService.getRideStats,
    refetchInterval: 30000,
  });

  // Fetch recent rides
  const { data: ridesData, isLoading: ridesLoading } = useQuery({
    queryKey: ['recentRides'],
    queryFn: () => rideService.getRides({ limit: 5, page: 1 }),
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (ridesData?.rides) {
      setRecentRides(ridesData.rides);
    }
  }, [ridesData]);

  // Fetch WhatsApp status
  const { isLoading: waLoading } = useQuery({
    queryKey: ['whatsappStatus'],
    queryFn: whatsappService.getStatus,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const handleViewAllRides = () => {
    clearNewRideCount();
    navigate('/rides');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'qualified':
        return 'secondary';
      case 'contacted':
        return 'info';
      case 'accepted':
        return 'info';
      case 'booked':
        return 'primary';
      case 'completed':
        return 'success';
      case 'rejected':
        return 'error';
      case 'expired':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (statsLoading || ridesLoading || waLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      {/* New Rides Alert */}
      {newRideCount > 0 && (
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleViewAllRides}>
              VIEW
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {newRideCount} new ride request{newRideCount > 1 ? 's' : ''} received!
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CarIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Total Rides</Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {stats?.total || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All time requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PendingIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">Pending</Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {stats?.pending || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Awaiting response
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Booked</Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {stats?.booked || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Confirmed rides
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CompletedIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6">Completed</Typography>
              </Box>
              <Typography variant="h4" color="success.main">
                {stats?.completed || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Finished rides
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DriversIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">Online Drivers</Typography>
              </Box>
              <Typography variant="h4" color="secondary.main">
                {onlineDrivers?.count ?? '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Currently available
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* AI Queue Controls */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AiIcon sx={{ mr: 1 }} />
                <Typography variant="h6">AI Processing Controls</Typography>
              </Box>
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Message queue</Typography>
                  <Tooltip title={aiStatus?.isPaused ? 'Queue is paused — click to resume' : 'Queue is running'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!aiStatus?.isPaused}
                          onChange={(e) => queueToggle.mutate(e.target.checked)}
                          color="success"
                          size="small"
                          disabled={queueToggle.isLoading}
                        />
                      }
                      label={aiStatus?.isPaused ? 'Paused' : 'Running'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Tooltip>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">AI analysis</Typography>
                  <Tooltip title={aiStatus?.aiPaused ? 'AI is paused — messages queue without analysis' : 'AI is active'}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!aiStatus?.aiPaused}
                          onChange={(e) => aiToggle.mutate(e.target.checked)}
                          color="success"
                          size="small"
                          disabled={aiToggle.isLoading}
                        />
                      }
                      label={aiStatus?.aiPaused ? 'Paused' : 'Active'}
                      labelPlacement="start"
                      sx={{ mr: 0 }}
                    />
                  </Tooltip>
                </Box>
                {aiStatus?.openAiFallbackEnabled && (
                  <Chip label="OpenAI fallback ON" color="info" size="small" sx={{ alignSelf: 'flex-start' }} />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Queue size</Typography>
                  <Typography variant="body2">{aiStatus?.queueSize ?? '—'}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* WhatsApp Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WhatsAppIcon sx={{ mr: 1 }} />
                <Typography variant="h6">WhatsApp Status</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Connection:</Typography>
                  <Chip
                    label={whatsappStatus?.isConnected ? 'Connected' : 'Disconnected'}
                    color={whatsappStatus?.isConnected ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Status:</Typography>
                  <Chip
                    label={whatsappStatus?.isReady ? 'Ready' : 'Not Ready'}
                    color={whatsappStatus?.isReady ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                {whatsappStatus?.lastActivity && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">Last Activity:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(whatsappStatus.lastActivity)}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/whatsapp')}
                startIcon={<WhatsAppIcon />}
              >
                Manage WhatsApp
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Live Messages */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <MessageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Live Messages</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Today's Messages:</Typography>
                  <Chip
                    label={liveMessageCount}
                    color="primary"
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Status:</Typography>
                  <Chip
                    label={whatsappStatus?.isReady ? 'Monitoring' : 'Offline'}
                    color={whatsappStatus?.isReady ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </Box>

              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setLiveMessageCount(0);
                  navigate('/live-messages');
                }}
                startIcon={<MessageIcon />}
              >
                View Live Messages
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Rides */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Recent Rides</Typography>
                <Button size="small" onClick={handleViewAllRides}>
                  View All
                </Button>
              </Box>

              {recentRides.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                  No rides found
                </Typography>
              ) : (
                <List dense>
                  {recentRides.map((ride, index) => (
                    <React.Fragment key={ride._id}>
                      <ListItem>
                        <ListItemIcon>
                          <CarIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2">
                                {ride.aiAnalysis?.from || 'Unknown'} → {ride.aiAnalysis?.to || 'Unknown'}
                              </Typography>
                              <Chip
                                label={ride.status}
                                color={getStatusColor(ride.status) as any}
                                size="small"
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption">
                                {ride.contactInfo?.name || 'Unknown'}
                              </Typography>
                              <Typography variant="caption">
                                {formatDate(ride.createdAt)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < recentRides.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* AI Performance */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingIcon sx={{ mr: 1 }} />
                <Typography variant="h6">AI Performance</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Average Confidence:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {Math.round(stats?.averageConfidence || 0)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={stats?.averageConfidence || 0}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                AI confidence level in detecting ride requests. Higher values indicate better accuracy.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Analytics section (collapsible) ─────────────────────────── */}
      <Box mt={4}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ cursor: 'pointer', userSelect: 'none', mb: 1 }}
          onClick={() => setAnalyticsOpen((v) => !v)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoneyIcon color="primary" fontSize="small" />
            <Typography variant="h6" fontWeight={700}>Analytics</Typography>
          </Stack>
          {analyticsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Collapse in={analyticsOpen} timeout="auto" unmountOnExit>
          <Stack spacing={4}>
            {/* AI Cost */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <AttachMoneyIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>AI Processing Cost</Typography>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Days</InputLabel>
                  <Select value={costDays} label="Days" onChange={(e) => setCostDays(Number(e.target.value))}>
                    {[7, 14, 30].map((d) => <MenuItem key={d} value={d}>Last {d}d</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              {costLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : costError ? (
                <Alert severity="info">No cost data yet — messages will be recorded once the backend processes rides.</Alert>
              ) : (
                <>
                  <Grid container spacing={2} mb={2}>
                    {[
                      { label: 'Total messages', value: costData!.totals.totalMessages.toLocaleString(), sub: `last ${costDays} days` },
                      { label: 'Ride-related', value: costData!.totals.rideRelated.toLocaleString(), sub: `${costData!.totals.totalMessages > 0 ? Math.round((costData!.totals.rideRelated / costData!.totals.totalMessages) * 100) : 0}% hit rate` },
                      { label: 'Total AI cost', value: `$${costData!.totals.costUsd.toFixed(4)}`, sub: 'USD (OpenAI)' },
                    ].map((stat) => (
                      <Grid item xs={12} sm={4} key={stat.label}>
                        <Card variant="outlined">
                          <CardContent>
                            <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                            <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                            <Typography variant="caption" color="text.secondary">{stat.sub}</Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  <Paper variant="outlined" sx={{ p: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>Daily message volume</Typography>
                    <AnalyticsBarChart rows={costData!.rows} labelKey="_id" valueKey="totalMessages" color="#2196f3" />
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Daily AI cost (USD)</Typography>
                    <AnalyticsBarChart rows={costData!.rows} labelKey="_id" valueKey="costUsd" color="#ff9800" formatValue={(v) => `$${v.toFixed(4)}`} />
                  </Paper>
                </>
              )}
            </Box>

            {/* Heatmap */}
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LocationOnIcon color="error" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Route Heatmap</Typography>
                </Stack>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Days</InputLabel>
                  <Select value={heatmapDays} label="Days" onChange={(e) => setHeatmapDays(Number(e.target.value))}>
                    {[7, 14, 30, 60].map((d) => <MenuItem key={d} value={d}>Last {d}d</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              {heatmapLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : (
                <Grid container spacing={3}>
                  {[
                    { title: 'Top pickup zones', key: 'pickups' as const, color: '#4caf50', labelKey: 'location' },
                    { title: 'Top dropoff zones', key: 'dropoffs' as const, color: '#e91e63', labelKey: 'location' },
                  ].map(({ title, key, color, labelKey }) => (
                    <Grid item xs={12} md={6} key={key}>
                      <Paper variant="outlined" sx={{ p: 3 }}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>{title}</Typography>
                        {heatmap?.[key]?.length ? (
                          <AnalyticsBarChart rows={heatmap[key]} labelKey={labelKey} valueKey="count" color={color} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">No data yet</Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Group activity</Typography>
                      {heatmap?.groupActivity?.length ? (
                        <AnalyticsBarChart rows={heatmap.groupActivity} labelKey="group" valueKey="count" color="#9c27b0" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">No data yet</Typography>
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              )}
            </Box>

            {/* Group suggestions */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <GroupsIcon2 color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>WhatsApp Group Suggestions</Typography>
              </Stack>
              {suggestionsLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
              ) : (
                <Paper variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Group</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">Rides</TableCell>
                        <TableCell align="right">Avg confidence</TableCell>
                        <TableCell align="right">Score</TableCell>
                        <TableCell>Recommendation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!suggestions?.suggestions?.length ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="text.secondary" py={2}>
                              No ride data yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : suggestions.suggestions.map((s: any) => (
                        <TableRow key={s.groupId || s.groupName} hover>
                          <TableCell>{s.groupName}</TableCell>
                          <TableCell>{s.accountName || '—'}</TableCell>
                          <TableCell align="right">{s.rideCount}</TableCell>
                          <TableCell align="right">{s.avgConfidence}%</TableCell>
                          <TableCell align="right"><strong>{s.score}</strong></TableCell>
                          <TableCell>
                            <Chip label={s.recommendation} size="small" color={RECOMMENDATION_COLOR[s.recommendation]} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              )}
            </Box>
          </Stack>
        </Collapse>
      </Box>
    </Box>
  );
};

export default Dashboard;
